import { Body, Box, Heading, Icon, Loader } from '@hover/blueprint';
import { iLock } from '@hover/icons';
import { captureException, captureMessage } from '@sentry/react';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router';

import { useProfessionalSignUp } from 'src/modules/app/hooks';
import type { Jwt } from 'src/shared/graphql/generated';
import { useAuthenticateWithCookieMutation } from 'src/shared/graphql/generated';
import { isNetworkError } from 'src/shared/helpers/graphql';
import { baseUrlToRedirectUrl } from 'src/shared/helpers/url';
import { useTranslate } from 'src/shared/hooks';
import { TranslationNamespace } from 'src/shared/hooks/useTranslate';
import { TwoColumn } from 'src/shared/layouts';
import { Route } from 'src/shared/route';
import { identify } from 'src/shared/telemetry';

/**
 * We consider user accounts that were created before November 1st (PST)
 * "legacy" or "grandfathered" SSO users and they should never see the
 * complete profile form regardless of whether we have all their info
 *
 * @see https://hoverinc.atlassian.net/browse/CX-2929
 */
const LEGACY_CUTOFF = new Date('2020-11-01T07:00:00.000Z');

const WEB_VIEW_RETRY_DELAY = 500;
const BRANCH_DEEP_LINK = 'https://hover.app.link/';
const BEACON_BRANCH_DEEP_LINK = 'https://hbeacon.app.link/';

const getBranchDeepLink = () => {
  const urlSearchParams = new URLSearchParams(window.location.search);
  return urlSearchParams.get('beacon') === '1'
    ? BEACON_BRANCH_DEEP_LINK
    : BRANCH_DEEP_LINK;
};

const isExternalSSOLogin = () => {
  const urlSearchParams = new URLSearchParams(window.location.search);
  return urlSearchParams.get('new-login') === '2';
};

const deepLinkToApp = ({
  accessToken,
  refreshToken,
}: Pick<Jwt, 'accessToken' | 'refreshToken'>) => {
  // query params with the word "access" are being filtered out via Branch's regex
  const url = `${getBranchDeepLink()}?accToken=${accessToken}&refreshToken=${refreshToken}`;
  window.location.href = url;
};

const SingleSignOnSuccess = ({ useNextStyle }: { useNextStyle?: boolean }) => {
  const tShared = useTranslate(TranslationNamespace.SHARED);
  const t = useTranslate(TranslationNamespace.LOGIN);
  const history = useHistory();
  const { userDispatch } = useProfessionalSignUp();

  const [hasIdentified, setHasIdentified] = useState(false);
  const [isWebView, setIsWebView] = useState<null | boolean>(null);
  const [openedDeepLink, setOpenDeepLink] = useState(false);

  const [authenticateWithCookie, { data: authenticateWithCookieData }] =
    useAuthenticateWithCookieMutation();

  useEffect(() => {
    const authenticate = async () => {
      try {
        await authenticateWithCookie();
      } catch (error) {
        // See DS-846
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        if (!isNetworkError(error)) {
          history.push(Route.LOGIN.path);
        }

        // TODO: expected authentication errors should probably be
        // handled in an Apollo error link at the client level

        // This is left here for now in case anything is unexpectedly going
        // wrong with the JWT hand off to the mobile application. Technically
        // we shouldn't be seeing authentication issues here until we start
        // redirecting all SSO login/signup traffic here.
        captureException(error);
      }
    };

    void authenticate();
  }, [authenticateWithCookie, history]);

  useEffect(() => {
    if (!authenticateWithCookieData) return;

    const id = async () => {
      const {
        authenticateWithCookie: {
          user: { email, id: userId, intercomSha },
        },
      } = authenticateWithCookieData;

      try {
        await identify({ userId, email, intercomSha });
      } catch (error) {
        captureException(error);
      } finally {
        setHasIdentified(true);
      }
    };

    void id();
  }, [authenticateWithCookieData]);

  useEffect(() => {
    if (isWebView) return undefined;

    if (window.ReactNativeWebView) {
      setIsWebView(true);
    } else {
      const id = setInterval(
        () => setIsWebView(!!window.ReactNativeWebView),
        WEB_VIEW_RETRY_DELAY,
      );

      return () => clearInterval(id);
    }

    return undefined;
  }, [isWebView]);

  useEffect(() => {
    if (!authenticateWithCookieData) return;

    if (isWebView) {
      if (window.ReactNativeWebView?.postMessage) {
        window.ReactNativeWebView.postMessage(
          JSON.stringify(authenticateWithCookieData.authenticateWithCookie),
        );
      } else {
        captureMessage('ReactNativeWebView: missing postMessage');
      }
    } else if (isExternalSSOLogin()) {
      if (!openedDeepLink) {
        const { jwt } = authenticateWithCookieData.authenticateWithCookie;
        setOpenDeepLink(true);
        deepLinkToApp(jwt);
      }
    } else if (isWebView === false && hasIdentified) {
      const { email, firstName, lastName, createdAt, mobilePhone, orgs } =
        authenticateWithCookieData.authenticateWithCookie.user;

      const createdAtDate = new Date(createdAt as string);
      const ssoOrg = orgs.find(org => org.ssoConfig?.ssoEnabled);

      if (ssoOrg && createdAtDate > LEGACY_CUTOFF && !mobilePhone) {
        userDispatch({
          type: 'UPDATE_EXTERNAL_PROFILE',
          payload: {
            company: ssoOrg.name ?? '',
            email,
            givenName: firstName ?? '',
            familyName: lastName ?? '',
            needsUserUpdate: true,
          },
        });

        history.push(
          Route.SIGN_UP_PROFESSIONAL_ORGANIZATION.path +
            new URL(window.location.href).search,
        );
      } else {
        window.location.href = baseUrlToRedirectUrl(window.location.href);
      }
    }
  }, [
    authenticateWithCookieData,
    hasIdentified,
    history,
    isWebView,
    userDispatch,
    openedDeepLink,
  ]);

  if (useNextStyle) {
    return (
      <Box
        alignItems="center"
        flexDir="column"
        height="100vh"
        justifyContent="center"
        width={1}
      >
        <Loader color="neutral.900" size="huge" />
        <Heading>{tShared('onboarding.signingIn')}</Heading>
      </Box>
    );
  }

  return (
    <TwoColumn.Main marginBottom={1000}>
      <TwoColumn.Header
        heading={t('singleSignOn.signedInWithSSO')}
        marginBottom={500}
        minWidth="23rem"
        subheading={t('singleSignOn.youveSuccessfullyAuthenticated')}
      />
      <Loader size="huge" />
      <Box alignItems="center" justifyContent="center" marginTop={500}>
        <Icon color="neutral600" icon={iLock} />
        <Body color="neutral700" margin={0} size={400} testId="sso-enabled">
          {t('singleSignOn.singleSignOnEnabled')}
        </Body>
      </Box>
    </TwoColumn.Main>
  );
};

export {
  SingleSignOnSuccess,
  deepLinkToApp,
  getBranchDeepLink,
  isExternalSSOLogin,
};
